import React, { useState, useRef, useEffect } from "react";
import { Video, StopCircle, Download, Play, Sparkles, Loader2, Trash2, Mic, MicOff } from "lucide-react";
import { aiQuick } from "../api";

export default function ScreenRecorder() {
  const [recording, setRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [withMic, setWithMic] = useState(true);
  const [recs, setRecs] = useState([]);
  const [aiOut, setAiOut] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [supported, setSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);

  const livePreviewRef = useRef(null);

  useEffect(() => {
    setSupported(typeof navigator !== "undefined" && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function");
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Keep the live preview <video> in sync with the active screen stream.
  useEffect(() => {
    if (livePreviewRef.current && streamRef.current && recording) {
      try { livePreviewRef.current.srcObject = streamRef.current; } catch { /* noop */ }
    }
  }, [recording]);

  const start = async () => {
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });
      let combined = displayStream;
      if (withMic) {
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          const ctx = new AudioContext();
          const dest = ctx.createMediaStreamDestination();
          [displayStream, mic].forEach(s => {
            const audioTracks = s.getAudioTracks();
            if (audioTracks.length) {
              const src = ctx.createMediaStreamSource(new MediaStream([audioTracks[0]]));
              src.connect(dest);
            }
          });
          combined = new MediaStream([
            ...displayStream.getVideoTracks(),
            ...dest.stream.getAudioTracks(),
          ]);
        } catch { /* mic denied — keep display only */ }
      }
      streamRef.current = combined;
      const mr = new MediaRecorder(combined, { mimeType: "video/webm;codecs=vp9,opus" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        setRecs(rs => [{ id: Date.now(), url, dur: duration, ts: new Date().toISOString() }, ...rs]);
        streamRef.current?.getTracks().forEach(t => t.stop());
      };
      // Stop when user clicks browser stop sharing button
      displayStream.getVideoTracks()[0].onended = () => stop();
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setDuration(0);
      startTsRef.current = Date.now();
      tickRef.current = setInterval(() => setDuration(Math.floor((Date.now() - startTsRef.current) / 1000)), 250);
    } catch (e) {
      alert("Screen capture cancelled or unavailable.");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const analyze = async () => {
    setAiBusy(true);
    setAiOut("");
    try {
      const r = await aiQuick(
        `Description of a screen recording: ${aiPrompt || "(no description provided)"}. Duration: ${duration}s. Generate suggested chapters, title, summary, and 3 hashtags for sharing.`,
        "You are an AI video assistant. Be concise and structured.",
      );
      setAiOut(r.text);
    } catch { setAiOut("AI unavailable."); }
    setAiBusy(false);
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div className="h-full flex" data-testid="app-recorder">
      <div className="flex-1 flex flex-col">
        <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video size={14} color="#8b5cf6" />
            <span className="text-sm font-medium">Screen Share &amp; Record</span>
            {recording && (
              <span className="ml-2 text-xs flex items-center gap-1 text-red-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                LIVE · {fmt(duration)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setWithMic(!withMic)}
              data-testid="rec-mic-toggle"
              className={`btn-ghost text-xs flex items-center gap-1 ${withMic ? "text-[#8b5cf6]" : "text-white/50"}`}
            >
              {withMic ? <Mic size={12} /> : <MicOff size={12} />} {withMic ? "Mic on" : "Mic off"}
            </button>
            {!recording ? (
              <button data-testid="rec-start" disabled={!supported} onClick={start} className="btn-accent text-xs flex items-center gap-1">
                <Video size={12} /> Share my screen
              </button>
            ) : (
              <button data-testid="rec-stop" onClick={stop} className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1">
                <StopCircle size={12} /> Stop sharing
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          {!supported && (
            <div className="text-xs text-amber-400 mb-3">Your browser doesn't support screen capture. Use Chrome, Edge or Firefox on desktop.</div>
          )}
          {recording && (
            <div data-testid="rec-live-preview" className="mb-4">
              <div className="mono text-[10px] uppercase tracking-widest text-[#8b5cf6] mb-1">// live · what others see</div>
              <video
                ref={livePreviewRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-[55vh] rounded-lg border border-[#8b5cf6]/40 bg-black"
              />
            </div>
          )}
          {recordedUrl ? (
            <div className="space-y-3">
              <video src={recordedUrl} controls className="w-full max-h-[55vh] rounded-lg border border-white/10" data-testid="rec-preview" />
              <div className="flex items-center gap-2">
                <a href={recordedUrl} download={`hexa-rec-${Date.now()}.webm`} className="btn-accent text-xs flex items-center gap-1">
                  <Download size={12} /> Download .webm
                </a>
                <button onClick={() => setRecordedUrl("")} className="btn-ghost text-xs">Clear</button>
              </div>
            </div>
          ) : !recording && (
            <div className="text-center py-12">
              <button
                data-testid="rec-hero-start"
                disabled={!supported}
                onClick={start}
                className="inline-flex items-center gap-3 px-6 py-4 text-base font-medium border border-[#8b5cf6]/40 hover:border-[#8b5cf6] hover:bg-[#8b5cf6]/8 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(160deg, rgba(139,92,246,0.12), rgba(167,139,250,0.08))",
                  clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
                }}
              >
                <Video size={22} className="text-[#8b5cf6]" />
                <span className="text-white">Share my screen</span>
              </button>
              <div className="text-xs text-white/55 mt-4">
                Pick a window, browser tab, or your full screen.<br />
                The session is captured and saved locally as a downloadable .webm — nothing is uploaded.
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-md mx-auto text-[10px] mono uppercase tracking-widest text-white/45">
                <div className="border border-white/10 p-3"><div className="text-[#8b5cf6] text-base font-medium normal-case mb-1">Window</div>a single app window</div>
                <div className="border border-white/10 p-3"><div className="text-[#a78bfa] text-base font-medium normal-case mb-1">Tab</div>just one browser tab</div>
                <div className="border border-white/10 p-3"><div className="text-[#3b82f6] text-base font-medium normal-case mb-1">Screen</div>your entire desktop</div>
              </div>
            </div>
          )}

          <div className="mt-6 p-3 border border-white/10 rounded-lg bg-black/30">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
              <Sparkles size={11} color="#ff7a2e" /> HEXA Video Tools
            </div>
            <textarea
              data-testid="rec-ai-desc"
              rows={2}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe what you recorded (e.g. 'demo of new dashboard feature')"
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-xs"
            />
            <button data-testid="rec-ai-analyze" disabled={aiBusy} onClick={analyze} className="btn-accent text-xs mt-2 flex items-center gap-1">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate chapters & title
            </button>
            {aiOut && <div className="text-xs whitespace-pre-wrap mt-2 bg-white/5 border border-white/10 rounded p-2">{aiOut}</div>}
          </div>
        </div>
      </div>

      <div className="w-56 border-l border-white/10 bg-black/30 flex flex-col">
        <div className="px-3 py-2 border-b border-white/10 text-xs uppercase tracking-widest text-white/40 flex items-center justify-between">
          <span>Recent</span>
          {recs.length > 0 && <button onClick={() => setRecs([])}><Trash2 size={10} className="text-red-400" /></button>}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {recs.length === 0 && <div className="text-xs text-white/40 text-center mt-6">No recordings</div>}
          {recs.map(r => (
            <button
              key={r.id}
              onClick={() => setRecordedUrl(r.url)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 flex items-center gap-2"
            >
              <Play size={11} className="text-[#ff7a2e]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs truncate">Rec {new Date(r.ts).toLocaleTimeString()}</div>
                <div className="text-[10px] text-white/40">{fmt(r.dur)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
